/**
 * Google Maps URL utilities for generating navigation links
 * 
 * We group up to 10 destinations per link. For routes with more stops,
 * we split into multiple links where each
 * subsequent link starts from the previous link's last stop.
 */

// Every URL this module produces starts with this prefix. Callers should
// check this immediately before window.open() as a defense-in-depth guard,
// independent of this file staying correct.
const GOOGLE_MAPS_URL_PREFIX = "https://www.google.com/maps/dir/";

/**
 * Confirm a URL is a Google Maps directions link before opening it.
 *
 * @param {string} url
 * @returns {boolean}
 */
export function isGoogleMapsDirectionsUrl(url) {
  return typeof url === "string" && url.startsWith(GOOGLE_MAPS_URL_PREFIX);
}

/**
 * Build a Google Maps directions URL from a list of addresses
 * 
 * @param {string[]} addresses - List of addresses in route order
 * @param {string|{lat:number,lng:number}} startAddress - Explicit observed starting point
 * @returns {string} Google Maps URL
 */
export function buildGoogleMapsUrl(addresses, startAddress = null) {
  if (!addresses || addresses.length === 0) {
    return "";
  }

  const origin = typeof startAddress === 'string' ? startAddress.trim()
    : typeof startAddress?.lat === 'number' && Number.isFinite(startAddress.lat) && Math.abs(startAddress.lat) <= 90
      && typeof startAddress?.lng === 'number' && Number.isFinite(startAddress.lng) && Math.abs(startAddress.lng) <= 180
      ? `${startAddress.lat},${startAddress.lng}` : '';
  if (!origin) return '';

  // Google Maps URL format: https://www.google.com/maps/dir/origin/waypoint1/.../destination
  const encodedOrigin = encodeURIComponent(origin);
  const encodedAddresses = addresses.map(addr => encodeURIComponent(addr));

  const urlParts = [encodedOrigin, ...encodedAddresses];
  const urlPath = urlParts.join("/");

  return `${GOOGLE_MAPS_URL_PREFIX}${urlPath}`;
}

/**
 * Build a full address string from an order object
 * 
 * @param {Object} order - Order object with address fields
 * @returns {string} Full address string
 */
export function getFullAddress(order) {
  if (!order) return "";
  
  const parts = [
    order.recipient_street_address,
    order.recipient_street_address_2,
    order.recipient_city,
    order.recipient_state,
    order.recipient_zip_code
  ].filter(Boolean);
  
  return parts.join(", ");
}

/**
 * Build "Resume from here" URL — starts from the previous stop's address
 * (or the shop, if resuming from the first stop) and continues through the
 * remaining stops.
 *
 * Keep construction synchronous so callers can open within the click gesture.
 * Browser/native-app handoff remains controlled by the device and Google Maps.
 *
 * @param {Object[]} stops - Array of all stops with order data
 * @param {number} fromIndex - 0-based index of the stop to resume from
 * @param {string|{lat:number,lng:number}} shopOrigin - Configured shop observation
 * @returns {Object} { url, stopCount, totalRemaining, needsSplit, addresses }
 */
export function buildResumeRouteFromStop(stops, fromIndex, shopOrigin = null) {
  if (!stops || fromIndex < 0 || fromIndex >= stops.length) {
    return { url: "", stopCount: 0, addresses: [] };
  }

  // Remaining stops to visit, in route order.
  const remaining = stops.slice(fromIndex);
  const addresses = remaining
    .map((stop) => getFullAddress(stop.order))
    .filter(Boolean);

  if (addresses.length === 0) {
    return { url: "", stopCount: 0, addresses: [] };
  }

  // Resume-from-first-stop ⇒ origin is the shop, matching the behavior of
  // the "Open in Google Maps" button. Resume-from-stop-N (N>0) ⇒ origin is
  // the address of stop N-1 in route order. If the previous stop has no
  // address, use the observed shop. Never substitute a built-in location.
  let origin = shopOrigin;
  if (fromIndex > 0) {
    origin = getFullAddress(stops[fromIndex - 1].order) || shopOrigin;
  }

  // Preserve our existing ten-destination grouping.
  const limited = addresses.slice(0, 10);
  const url = buildGoogleMapsUrl(limited, origin);

  return {
    url,
    stopCount: limited.length,
    totalRemaining: addresses.length,
    needsSplit: addresses.length > 10,
    addresses: limited,
  };
}

/**
 * Split a route into multiple Google Maps URL segments
 * Each segment has max 10 waypoints. Subsequent segments start from
 * the previous segment's last stop for continuity.
 * 
 * @param {Object[]} stops - Array of populated stop objects with order data
 * @param {number} maxWaypoints - Maximum stops per segment (default 10)
 * @returns {Object[]} Array of segment objects with url, label, stopRange
 */
export function splitRouteForGoogleMaps(stops, maxWaypoints = 10, shopOrigin = null) {
  if (!stops || stops.length === 0) {
    return [];
  }

  // Extract addresses from stops
  const addresses = stops.map(stop => getFullAddress(stop.order)).filter(Boolean);
  
  if (addresses.length === 0) {
    return [];
  }

  const results = [];
  let startIdx = 0;
  let segmentNum = 1;

  while (startIdx < addresses.length) {
    const endIdx = Math.min(startIdx + maxWaypoints, addresses.length);
    const segmentAddresses = addresses.slice(startIdx, endIdx);

    // First segment: Shop → Stops
    // Subsequent segments: Previous last stop → Next stops
    let url;
    if (segmentNum === 1) {
      url = buildGoogleMapsUrl(segmentAddresses, shopOrigin);
    } else {
      const prevEndAddress = addresses[startIdx - 1];
      url = buildGoogleMapsUrl(segmentAddresses, prevEndAddress);
    }

    const isOnlySegment = addresses.length <= maxWaypoints;
    
    results.push({
      segment: segmentNum,
      url,
      startStop: startIdx + 1,  // 1-based for display
      endStop: endIdx,
      stopCount: segmentAddresses.length,
      label: isOnlySegment 
        ? "Open in Google Maps" 
        : `Part ${segmentNum}: Stops ${startIdx + 1}-${endIdx}`
    });

    startIdx = endIdx;
    segmentNum += 1;
  }

  return results;
}
